/**
 * Button: mint GeoSim gst + open visitor preview for a country (super-admin).
 * Replaces bare `/?country=XX` links that skip Worker edge simulation.
 *
 * Ported from delfuocampo GeoSimCountryPreviewButton — same UX, anaa mint API.
 */

import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { openAdminCountryGeoSimPreview } from "@/lib/geoSim/openAdminCountryPreview";
import { cn } from "@/lib/utils";
import { ExternalLink, Loader2 } from "lucide-react";
import { useCallback, useState, type ComponentProps, type ReactNode } from "react";

type ButtonVariant = ComponentProps<typeof Button>["variant"];
type ButtonSize = ComponentProps<typeof Button>["size"];

export type GeoSimCountryPreviewButtonProps = {
  country: string;
  /** Default `/` (home DualCTA / Base). */
  path?: string;
  label?: ReactNode;
  title?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  /** Show external-link icon (default true). */
  showIcon?: boolean;
  province?: string;
  city?: string;
  state?: string;
  /** Analytics source (e.g. country_templates_row). */
  source?: string;
  onOpened?: (url: string) => void;
};

export function GeoSimCountryPreviewButton({
  country,
  path = "/",
  label = "GeoSim",
  title,
  variant = "outline",
  size = "sm",
  className,
  showIcon = true,
  province,
  city,
  state,
  source = "admin_country_preview_button",
  onOpened,
}: GeoSimCountryPreviewButtonProps) {
  const { getAuthHeaders, isSignedIn, isLoaded } = useAdminAuth();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const cc = (country || "").toUpperCase().slice(0, 2);

  const handleClick = useCallback(async () => {
    if (!isLoaded) return;
    if (!isSignedIn) {
      toast({
        title: "Sesión admin requerida",
        description: "Iniciá sesión de super-admin para mint de GeoSim (gst).",
        variant: "destructive",
      });
      return;
    }
    setBusy(true);
    try {
      const result = await openAdminCountryGeoSimPreview({
        country: cc,
        path,
        province,
        city,
        state,
        openInNewTab: true,
        source,
        getAuthHeaders,
      });
      if (!result.ok) {
        toast({
          title: "No se pudo abrir preview",
          description: result.error,
          variant: "destructive",
        });
        return;
      }
      onOpened?.(result.url);
      toast({
        title: `Preview ${result.country}`,
        description: `GeoSim Clean · edge real · template: ${result.expectedTemplate}`,
      });
    } catch (e) {
      toast({
        title: "Error al abrir preview",
        description: e instanceof Error ? e.message : "Error desconocido",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  }, [
    cc,
    city,
    getAuthHeaders,
    isLoaded,
    isSignedIn,
    onOpened,
    path,
    province,
    source,
    state,
    toast,
  ]);

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      className={cn(className)}
      disabled={busy || !isLoaded}
      title={
        title ||
        `Simular visitante ${cc} con GeoSim (edge real — no solo ?country=)`
      }
      onClick={() => void handleClick()}
    >
      {busy ? (
        <Loader2
          className={cn("animate-spin", showIcon ? "mr-1 h-3.5 w-3.5" : "h-3.5 w-3.5")}
        />
      ) : showIcon ? (
        <ExternalLink className="mr-1 h-3.5 w-3.5" />
      ) : null}
      {label}
    </Button>
  );
}
